import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getVehicleSpecs, saveVehicleSpecs } from '../api';
import type { VehicleSpecs } from '../api';
import { useToast } from '../context/ToastContext';
import { useSettings } from '../context/SettingsContext';
import { barToDisplay, displayToBar } from '../utils/units';

const empty: VehicleSpecs = {
  front_axle: '',
  rear_axle: '',
  bolt_pattern: '',
  center_bore: '',
  tyre_pressure_front: undefined,
  tyre_pressure_rear: undefined,
  oil_type: '',
  oil_capacity: undefined,
  wiper_front: '',
  wiper_rear: '',
  notes: '',
};

interface Props {
  vehicleId: number;
  viewOnly?: boolean;
  onClose?: () => void;
}

export default function SpecsTab({ vehicleId, viewOnly = false, onClose }: Props) {
  const { t } = useTranslation();
  const { showError, showSuccess } = useToast();
  const { settings } = useSettings();
  const p = settings.pressureUnit;
  const l = settings.lengthUnit;
  const [form, setForm] = useState<VehicleSpecs>(empty);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(() => {
    setLoading(true);
    getVehicleSpecs(vehicleId)
      .then(data => {
        if (!data) { setForm(empty); return; }
        setForm({
          ...data,
          tyre_pressure_front:        barToDisplay(data.tyre_pressure_front,        p),
          tyre_pressure_rear:         barToDisplay(data.tyre_pressure_rear,         p),
          tyre_pressure_front_loaded: barToDisplay(data.tyre_pressure_front_loaded, p),
          tyre_pressure_rear_loaded:  barToDisplay(data.tyre_pressure_rear_loaded,  p),
          tyre_pressure_standard:     barToDisplay(data.tyre_pressure_standard,     p),
        });
      })
      .catch(() => setForm(empty))
      .finally(() => setLoading(false));
  }, [vehicleId, p]);

  useEffect(() => { loadData(); }, [loadData]);

  const set = (field: keyof VehicleSpecs, value: unknown) =>
    setForm(f => ({ ...f, [field]: value }));

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const toSave: VehicleSpecs = {
        ...form,
        tyre_pressure_front:        displayToBar(form.tyre_pressure_front,        p),
        tyre_pressure_rear:         displayToBar(form.tyre_pressure_rear,         p),
        tyre_pressure_front_loaded: displayToBar(form.tyre_pressure_front_loaded, p),
        tyre_pressure_rear_loaded:  displayToBar(form.tyre_pressure_rear_loaded,  p),
        tyre_pressure_standard:     displayToBar(form.tyre_pressure_standard,     p),
      };
      await saveVehicleSpecs(vehicleId, toSave);
      showSuccess(t('specs.saved'));
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Fejl ved gem');
    }
    setSaving(false);
  };

  if (loading) return <div className="p-6 text-sm text-gray-400">{t('vehicles.loading')}</div>;

  const inp = 'input disabled:bg-gray-50 disabled:text-gray-600';

  return (
    <form onSubmit={handleSave} className="p-6 space-y-4">

      {/* Dæk og fælge */}
      <h3 className="card-heading">{t('specs.tyres_section')}</h3>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('specs.front_axle')}</label>
          <input disabled={viewOnly} className={inp} value={form.front_axle ?? ''}
            onChange={e => set('front_axle', e.target.value)} placeholder="f.eks. 205/55 R16" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('specs.rear_axle')}</label>
          <input disabled={viewOnly} className={inp} value={form.rear_axle ?? ''}
            onChange={e => set('rear_axle', e.target.value)} placeholder="f.eks. 205/55 R16" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('specs.bolt_pattern')}</label>
          <input disabled={viewOnly} className={inp} value={form.bolt_pattern ?? ''}
            onChange={e => set('bolt_pattern', e.target.value)} placeholder="f.eks. 4x108" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('specs.center_bore')}</label>
          <input disabled={viewOnly} className={inp} value={form.center_bore ?? ''}
            onChange={e => set('center_bore', e.target.value)} placeholder="f.eks. 65,1" />
        </div>
      </div>

      {/* Tyre pressure */}
      <h3 className="card-heading">{t('specs.tyre_pressure_section')}</h3>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('specs.tyre_pressure_front')} ({p})</label>
          <input type="number" step="0.1" min="0" disabled={viewOnly} className={inp}
            value={form.tyre_pressure_front ?? ''}
            onChange={e => set('tyre_pressure_front', e.target.value ? +e.target.value : undefined)} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('specs.tyre_pressure_rear')} ({p})</label>
          <input type="number" step="0.1" min="0" disabled={viewOnly} className={inp}
            value={form.tyre_pressure_rear ?? ''}
            onChange={e => set('tyre_pressure_rear', e.target.value ? +e.target.value : undefined)} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('specs.tyre_pressure_front_loaded')} ({p})</label>
          <input type="number" step="0.1" min="0" disabled={viewOnly} className={inp}
            value={form.tyre_pressure_front_loaded ?? ''}
            onChange={e => set('tyre_pressure_front_loaded', e.target.value ? +e.target.value : undefined)} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('specs.tyre_pressure_rear_loaded')} ({p})</label>
          <input type="number" step="0.1" min="0" disabled={viewOnly} className={inp}
            value={form.tyre_pressure_rear_loaded ?? ''}
            onChange={e => set('tyre_pressure_rear_loaded', e.target.value ? +e.target.value : undefined)} />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('specs.tyre_pressure_standard')} ({p})</label>
        <input type="number" step="0.1" min="0" disabled={viewOnly} className={inp}
          value={form.tyre_pressure_standard ?? ''}
          onChange={e => set('tyre_pressure_standard', e.target.value ? +e.target.value : undefined)} />
      </div>

      {/* Oil */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('specs.oil_type')}</label>
          <input disabled={viewOnly} className={inp} value={form.oil_type ?? ''}
            onChange={e => set('oil_type', e.target.value)} placeholder="f.eks. 5W-30" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('specs.oil_capacity')}</label>
          <input type="number" step="0.1" min="0" disabled={viewOnly} className={inp}
            value={form.oil_capacity ?? ''}
            onChange={e => set('oil_capacity', e.target.value ? +e.target.value : undefined)} />
        </div>
      </div>

      {/* Wipers */}
      <h3 className="card-heading">{t('specs.wipers_section')}</h3>
      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('specs.wiper_front_driver')} ({l})</label>
          <input disabled={viewOnly} className={inp} value={form.wiper_front ?? ''}
            onChange={e => set('wiper_front', e.target.value)} placeholder={l === 'mm' ? 'f.eks. 650' : 'f.eks. 26"'} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('specs.wiper_front_passenger')} ({l})</label>
          <input disabled={viewOnly} className={inp} value={form.wiper_front_passenger ?? ''}
            onChange={e => set('wiper_front_passenger', e.target.value)} placeholder={l === 'mm' ? 'f.eks. 400' : 'f.eks. 16"'} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('specs.wiper_rear')} ({l})</label>
          <input disabled={viewOnly} className={inp} value={form.wiper_rear ?? ''}
            onChange={e => set('wiper_rear', e.target.value)} placeholder={l === 'mm' ? 'f.eks. 280' : 'f.eks. 11"'} />
        </div>
      </div>

      {/* Notes */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('specs.notes')}</label>
        <textarea rows={9} disabled={viewOnly}
          className="input disabled:bg-gray-50 disabled:text-gray-600 resize-none"
          value={form.notes ?? ''}
          onChange={e => set('notes', e.target.value)} />
      </div>

      <div className="flex justify-end gap-3 pt-2">
        {!viewOnly && (
          <>
            <button type="button" onClick={loadData} disabled={saving}
              className="btn btn-secondary">
              {t('modal.cancel')}
            </button>
            <button type="submit" disabled={saving}
              className="btn btn-primary">
              {saving ? '...' : t('specs.save')}
            </button>
          </>
        )}
        {onClose && (
          <button type="button" onClick={onClose}
            className="btn btn-secondary">
            {t('modal.close')}
          </button>
        )}
      </div>
    </form>
  );
}
